import React, { useRef, useLayoutEffect, useState, useMemo } from "react";
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import PowerBIIcon from '../../../assests/power-bi-icon.svg';
import { MetricsPopover } from './MetricsPopover';

const NodeBox = React.forwardRef(function NodeBox({ text, status, isDataPulseProduct, isPowerBIReport, shouldBlink, onClick }, ref) {
  const statusClass = isPowerBIReport ? "status-powerbi"
    : isDataPulseProduct === false ? "status-default"
    : status === "critical" ? "status-critical"
      : status === "warning" ? "status-warning"
        : status === "healthy" ? "status-healthy"
          : "status-default";

  const nodeStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '2px solid white',
    boxShadow: '0 6px 14px rgba(15, 23, 42, 0.12)',
    ...(isDataPulseProduct === false && !isPowerBIReport ? { opacity: 0.5, filter: 'grayscale(1)' } : {}),
    ...(isPowerBIReport ? { color: '#111827' } : {}),
  };

  return (
    <div ref={ref} className={`node-box ${statusClass} cursor-pointer`} style={nodeStyle} onClick={onClick}>
      {isPowerBIReport && (
        <img src={PowerBIIcon} alt="Power BI" style={{ width: '18px', height: '18px', objectFit: 'contain', flexShrink: 0 }} />
      )}
      <div className="node-text">{text}</div>
      {shouldBlink && <div className="node-circular-note">(Circular dependency)</div>}
    </div>
  );
});

const UpwardTreeView = ({
  contractName,
  contractStatus,
  dependencies,
  contractProductId,
  contractSubscribedAssets = [],
  graphProductIds = new Set(),
  isFullScreen = false,
}) => {
  const popoverContainerRef = useRef(null);
  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const scaleWrapperRef = useRef(null);

  const nodeRefs = useRef({});
  const connectionsRef = useRef([]);
  const [paths, setPaths] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const [metricsVisible, setMetricsVisible] = useState(false);
  const [metricsNodeId, setMetricsNodeId] = useState(null);
  const [metricsNodeName, setMetricsNodeName] = useState('');
  const [metricsNodeDetails, setMetricsNodeDetails] = useState(null);

  connectionsRef.current = [];

  const root = useMemo(() => ({
    id: "_root_",
    name: contractName,
    status: contractStatus,
    isDataPulseProduct: true,
    isPowerBIReport: false,
    subscribedAssets: contractSubscribedAssets,
    dependencies: Array.isArray(dependencies) ? dependencies : []
  }), [contractName, contractStatus, dependencies, contractSubscribedAssets]);

  const registerConnection = (fromKey, toKey) => {
    connectionsRef.current.push({ fromKey, toKey });
  };

  const getNodeSubscribedAssets = (node) => {
    if (node?.id === "_root_") {
      return contractSubscribedAssets || [];
    }

    return node?.subscribedAssets || [];
  };

  const handleNodeClick = (node, event) => {
    event.stopPropagation();

    if (node.isPowerBIReport) {
      setMetricsVisible(false);
      return;
    }

    const resolvedMetricsNodeId = node.id === '_root_'
      ? (contractProductId || node.id)
      : node.id;

    setMetricsNodeId(resolvedMetricsNodeId);
    setMetricsNodeName(node.name || node.id);
    setMetricsNodeDetails({
      ...node,
      subscribedAssets: getNodeSubscribedAssets(node),
    });
    setMetricsVisible(true);
  };

  const handleCloseMetrics = () => {
    setMetricsVisible(false);
  };

  function RenderNode({ node, path = "0", visited = new Set() }) {
    if (!node) return null;

    const nodeId = node.id ?? node.name ?? `node-${path}`;
    const instanceKey = `${nodeId}#${path}`;
    const isRootNode = nodeId === "_root_";
    const shouldBlink = node.name?.trim() === contractName.trim() && !isRootNode;

    if (visited.has(nodeId)) {
      const assignRef = (el) => { if (el) nodeRefs.current[instanceKey] = el; };
      return (
        <div className="node-column">
          <div className={`node-instance ${shouldBlink ? "node-blink" : ""}`}>
            <NodeBox
              ref={assignRef}
              text={node.name ?? nodeId}
              status={node.status}
              isDataPulseProduct={node.isDataPulseProduct}
              isPowerBIReport={node.isPowerBIReport}
              shouldBlink={shouldBlink}
              onClick={(event) => handleNodeClick(node, event)}
            />
          </div>
        </div>
      );
    }

    const parents = Array.isArray(node.dependencies) ? node.dependencies : [];

    parents.forEach((p, idx) => {
      const fromKey = `${(p.id ?? p.name ?? `p-${path}-${idx}`)}#${path}-${idx}`;
      registerConnection(fromKey, instanceKey);
    });

    return (
      <div className="node-column">
        {parents.length > 0 && (
          <div className="parents-row">
            {parents.map((p, idx) => (
              <div key={`${(p.id ?? p.name ?? `p-${path}-${idx}`)}#${path}-${idx}`} className="parent-column">
                <RenderNode node={p} path={`${path}-${idx}`} visited={new Set([...visited, nodeId])} />
              </div>
            ))}
          </div>
        )}

        {parents.length > 0 && <div className="spacer" />}

        <div className={`node-instance ${shouldBlink ? "node-blink" : ""}`}>
          <NodeBox
            ref={(el) => { if (el) nodeRefs.current[instanceKey] = el; }}
            text={node.name ?? nodeId}
            status={node.status}
            isDataPulseProduct={node.isDataPulseProduct}
            isPowerBIReport={node.isPowerBIReport}
            shouldBlink={shouldBlink}
            onClick={(event) => handleNodeClick(node, event)}
          />
        </div>
      </div>
    );
  }

  const startDrag = (clientX, clientY) => {
    setIsDragging(true);
    dragStartRef.current = { x: clientX, y: clientY };
    panStartRef.current = { ...pan };
    document.body.style.userSelect = "none";
  };

  const onPointerDown = (e) => {
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (cx == null) return;
    startDrag(cx, cy);
  };

  const onPointerMove = (e) => {
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    if (cx == null) return;

    if (!isDragging) return;
    const dx = cx - dragStartRef.current.x;
    const dy = cy - dragStartRef.current.y;
    setPan({ x: panStartRef.current.x + dx, y: panStartRef.current.y + dy });
  };

  const onPointerUp = () => {
    setIsDragging(false);
    document.body.style.userSelect = "";
  };

  const handleZoomIn = () => setZoom(z => Math.min(2.5, +(z * 1.2).toFixed(3)));
  const handleZoomOut = () => setZoom(z => Math.max(0.4, +(z / 1.2).toFixed(3)));
  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const wrapper = scaleWrapperRef.current;
    if (!scrollEl || !wrapper) return;

    const containerEl = containerRef.current;
    if (containerEl && !containerEl.style.padding) containerEl.style.padding = "40px";

    let mounted = true;

    const computeAll = () => {
      if (!mounted) return;
      const wrapperRect = wrapper.getBoundingClientRect();

      const newPaths = [];
      for (const conn of connectionsRef.current) {
        const fromEl = nodeRefs.current[conn.fromKey];
        const toEl = nodeRefs.current[conn.toKey];
        if (!fromEl || !toEl) continue;

        const fRect = fromEl.getBoundingClientRect();
        const tRect = toEl.getBoundingClientRect();

        const sx1 = fRect.left + fRect.width / 2 - wrapperRect.left;
        const sy1 = fRect.top + fRect.height - wrapperRect.top;
        const sx2 = tRect.left + tRect.width / 2 - wrapperRect.left;
        const sy2 = tRect.top - wrapperRect.top;

        const x1 = sx1 / zoom;
        const y1 = sy1 / zoom;
        const x2 = sx2 / zoom;
        const y2 = sy2 / zoom;

        const midY = (y1 + y2) / 2;
        newPaths.push(`M ${x1} ${y1} C ${x1} ${midY} ${x2} ${midY} ${x2} ${y2}`);
      }

      const svgW = wrapperRect.width / zoom;
      const svgH = wrapperRect.height / zoom;

      if (!mounted) return;
      setPaths(newPaths);
      setSvgSize({ width: svgW, height: svgH });
    };

    computeAll();
    const raf = requestAnimationFrame(computeAll);

    const onScroll = () => requestAnimationFrame(computeAll);
    const onResize = () => requestAnimationFrame(computeAll);

    scrollEl.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      scrollEl.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [root, zoom, pan]);

  const transformStyle = { transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "0 0" };

  return (
    <div className={`relative overflow-hidden ${isFullScreen ? 'h-full' : ''}`} ref={popoverContainerRef}>
      <div className="absolute top-3 right-3 z-20 flex gap-2">
        <button onClick={handleZoomIn} className="p-2 bg-white border rounded shadow-sm"><ZoomIn className="  h-4 text-gray-600" /></button>
        <button onClick={handleZoomOut} className="p-2 bg-white border rounded shadow-sm"><ZoomOut className="  h-4 text-gray-600" /></button>
        <button onClick={handleReset} className="p-2 bg-white border rounded shadow-sm"> <RotateCcw className="  h-4 text-gray-600" /></button>
      </div>

      <div
        ref={scrollRef}
        className={`bg-white rounded-sm border border-gray-200 cursor-grab active:cursor-grabbing ${isFullScreen ? 'h-full' : ''}`}
        onMouseDown={onPointerDown}
        onMouseMove={onPointerMove}
        onMouseUp={onPointerUp}
        onMouseLeave={onPointerUp}
        onTouchStart={(e) => onPointerDown(e.touches ? e.touches[0] : e)}
        onTouchMove={(e) => onPointerMove(e.touches ? e.touches[0] : e)}
        onTouchEnd={onPointerUp}
      >
        <div className="upward-tree-root">
          <div className="upward-tree-container" ref={containerRef}>
            <div ref={scaleWrapperRef} className="upward-scale-wrapper" style={transformStyle}>
              <div className="upward-tree-center">
                <RenderNode node={root} path="root" visited={new Set()} />
              </div>

              <svg
                className="upward-svg w-full h-full"
                xmlns="http://www.w3.org/2000/svg"
                width={svgSize.width}
                height={svgSize.height}
                style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", overflow: "visible" }}
              >
                <g className="connector-group">
                  {paths.map((p, i) => (
                    <path key={i} d={p} className="connector-path" />
                  ))}
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <MetricsPopover
        key={metricsNodeId || 'metrics-panel'}
        visible={metricsVisible}
        nodeId={metricsNodeId}
        nodeName={metricsNodeName}
        nodeStatus={metricsNodeDetails?.status}
        isDataPulseProduct={metricsNodeDetails?.isDataPulseProduct}
        graphProductIds={graphProductIds}
        onClose={handleCloseMetrics}
      />
    </div>
  );
};

export default UpwardTreeView;
